import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "design-system/atoms/Accordion"
import { Button } from "design-system/atoms/Button"
import { SheetFooter } from "design-system/atoms/Sheet"
import { ensure } from "lopermut"
import { useKindBadge } from "modules/capabilities/hooks/useKindBadge"
import { useCallback, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  CapabilitiesMarketplaceResponseDataSchema,
  CapabilitiesResponseDataSchema,
  CapabilityCampaignAssignation,
  CapabilityIdentifier,
  CapabilityResponseDataSchema,
  ConversationFlowEnum,
  ReactToMessageCapabilityConfigSchema,
  RequestReviewCapabilityConfigSchema,
  SupportCapabilityConfigSchema,
} from "schema"
import { toast } from "sonner"
import z from "zod"
import { useCapabilitiesDictionary } from "../../hooks/useCapabilitiesDictionary"
import { useUpdateCapabilityActivationMutation } from "../../hooks/useUpdateCapabilityActivationMutation"
import { CapabilityActivationSettings } from "../CapabilityActivationSettings"
import { CapabilitySheetLayout } from "../CapabilitySheetLayout"
import { BusinessOpportunityForm } from "./BusinessOpportunityForm"
import { CancelOrderForm } from "./CancelOrderForm"
import { FindOrderNumberForm } from "./FindOrderNumberForm"
import { GetOrderRefundStatusForm } from "./GetOrderRefundStatusForm"
import { GetOrderTrackingForm } from "./GetOrderTrackingForm"
import { ProductInformationForm } from "./ProductInformationForm"
import { ReactToMessageForm } from "./ReactToMessageForm"
import { RequestReviewForm } from "./RequestReviewForm"
import { SearchCatalogForm } from "./SearchCatalogForm"
import { SupportForm } from "./SupportForm"
import { UpdateOrderForm } from "./UpdateOrderForm"

type Props = {
  capability:
    | z.infer<typeof CapabilitiesResponseDataSchema>[number]
    | z.infer<typeof CapabilitiesMarketplaceResponseDataSchema>[number]
  mode?: "install" | "configure"
  open?: boolean
  onOpenChange: (open: boolean) => void
  onConfigure?: (capability: z.infer<typeof CapabilityResponseDataSchema>) => void
}

export function CapabilityIntegrationDialog({
  capability,
  mode = "install",
  open = true,
  onOpenChange,
  onConfigure,
}: Props) {
  const { t } = useTranslation()
  const capabilitiesDictionary = useCapabilitiesDictionary()
  const getBadge = useKindBadge()

  const { name, detailedDescription, description } =
    capabilitiesDictionary[capability.identifier as keyof typeof capabilitiesDictionary]

  const onClose = useCallback(() => onOpenChange(false), [onOpenChange])

  const [campaignAssignation, setCampaignAssignation] = useState<CapabilityCampaignAssignation>(
    mode === "configure" && "campaignAssignation" in capability
      ? capability.campaignAssignation
      : CapabilityCampaignAssignation.AllCampaigns,
  )
  const [campaignFlow, setCampaignFlow] = useState<keyof typeof ConversationFlowEnum>(
    mode === "configure" && "campaignFlow" in capability && capability.campaignFlow
      ? capability.campaignFlow
      : ConversationFlowEnum.Inbound,
  )
  const [campaignIds, setCampaignIds] = useState<string[]>(
    mode === "configure" && "campaignIds" in capability ? capability.campaignIds : [],
  )
  const [isPending, setIsPending] = useState(false)
  const [isConfigureDialogOpen, setIsConfigureDialogOpen] = useState(false)
  const [installedCapability, setInstalledCapability] = useState<z.infer<
    typeof CapabilityResponseDataSchema
  > | null>(null)
  const formSubmitRef = useRef<
    | ((
        campaignAssignation: CapabilityCampaignAssignation,
        campaignIds: string[],
        campaignFlow?: "Inbound" | "Outbound",
      ) => Promise<void>)
    | null
  >(null)

  const { mutateAsync: updateActivation, isPending: isUpdateActivationPending } =
    useUpdateCapabilityActivationMutation()

  const handlePendingChange = useCallback((pending: boolean) => {
    setIsPending(pending)
  }, [])

  const handleConfigure = useCallback(
    (installed: z.infer<typeof CapabilityResponseDataSchema>) => {
      if (mode === "install") {
        if (onConfigure) {
          onConfigure(installed)
        } else {
          setInstalledCapability(installed)
          setIsConfigureDialogOpen(true)
          onClose()
        }
      }
    },
    [mode, onConfigure, onClose],
  )

  const handleSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault()
      if (!formSubmitRef.current) return

      await formSubmitRef.current(campaignAssignation, campaignIds, campaignFlow)

      if (mode === "configure") {
        await updateActivation({
          id: ensure("id" in capability ? capability.id : undefined),
          campaignAssignation,
          campaignIds,
          campaignFlow,
        })
        toast.success(t("capabilities.activation.success"))
        onClose()
      }
    },
    [
      campaignAssignation,
      campaignIds,
      campaignFlow,
      mode,
      capability,
      updateActivation,
      t,
      onClose,
    ],
  )

  const initialConfig = useMemo(() => {
    if (mode === "configure" && "config" in capability && capability.config) {
      return capability.config as {
        provider?: string
        shouldAutoRefund?: boolean
        shouldRestock?: boolean
        cancellationWindowMinutes?: number | null
        organizationPhoneNumbers?: string | null
        activationInstructions?: string
        responseInstructions?: string
      }
    }
    return undefined
  }, [mode, capability])

  const content = useMemo(() => {
    switch (capability.identifier) {
      case CapabilityIdentifier.FindOrderNumber:
        return (
          <FindOrderNumberForm
            capability={capability as z.infer<typeof CapabilitiesResponseDataSchema>[number]}
            mode={mode === "configure" ? "update" : "install"}
            initialConfig={initialConfig}
            onClose={mode === "configure" ? () => {} : onClose}
            onPendingChange={handlePendingChange}
            onConfigure={handleConfigure}
            onSubmitHandlerRef={formSubmitRef}
          />
        )
      case CapabilityIdentifier.GetOrderTracking:
        return (
          <GetOrderTrackingForm
            capability={capability as z.infer<typeof CapabilitiesResponseDataSchema>[number]}
            mode={mode === "configure" ? "update" : "install"}
            initialConfig={initialConfig}
            onClose={mode === "configure" ? () => {} : onClose}
            onPendingChange={handlePendingChange}
            onConfigure={handleConfigure}
            onSubmitHandlerRef={formSubmitRef}
          />
        )
      case CapabilityIdentifier.CancelOrder:
        return (
          <CancelOrderForm
            capability={capability as z.infer<typeof CapabilitiesResponseDataSchema>[number]}
            mode={mode === "configure" ? "update" : "install"}
            initialConfig={initialConfig}
            onClose={mode === "configure" ? () => {} : onClose}
            onPendingChange={handlePendingChange}
            onConfigure={handleConfigure}
            onSubmitHandlerRef={formSubmitRef}
          />
        )
      case CapabilityIdentifier.UpdateOrder:
        return (
          <UpdateOrderForm
            capability={capability as z.infer<typeof CapabilitiesResponseDataSchema>[number]}
            mode={mode === "configure" ? "update" : "install"}
            initialConfig={initialConfig}
            onClose={mode === "configure" ? () => {} : onClose}
            onPendingChange={handlePendingChange}
            onConfigure={handleConfigure}
            onSubmitHandlerRef={formSubmitRef}
          />
        )
      case CapabilityIdentifier.GetOrderRefundStatus:
        return (
          <GetOrderRefundStatusForm
            capability={capability as z.infer<typeof CapabilitiesResponseDataSchema>[number]}
            mode={mode === "configure" ? "update" : "install"}
            initialConfig={initialConfig}
            onClose={mode === "configure" ? () => {} : onClose}
            onPendingChange={handlePendingChange}
            onConfigure={handleConfigure}
            onSubmitHandlerRef={formSubmitRef}
          />
        )
      case CapabilityIdentifier.SearchCatalog:
        return (
          <SearchCatalogForm
            capability={capability as z.infer<typeof CapabilitiesResponseDataSchema>[number]}
            mode={mode === "configure" ? "update" : "install"}
            initialConfig={initialConfig}
            onClose={mode === "configure" ? () => {} : onClose}
            onPendingChange={handlePendingChange}
            onConfigure={handleConfigure}
            onSubmitHandlerRef={formSubmitRef}
          />
        )
      case CapabilityIdentifier.ProductInformation:
        return (
          <ProductInformationForm
            capability={capability as z.infer<typeof CapabilitiesResponseDataSchema>[number]}
            mode={mode === "configure" ? "update" : "install"}
            initialConfig={initialConfig}
            onClose={mode === "configure" ? () => {} : onClose}
            onPendingChange={handlePendingChange}
            onConfigure={handleConfigure}
            onSubmitHandlerRef={formSubmitRef}
          />
        )
      case CapabilityIdentifier.BusinessOpportunity:
        return (
          <BusinessOpportunityForm
            capability={capability as z.infer<typeof CapabilitiesResponseDataSchema>[number]}
            mode={mode === "configure" ? "update" : "install"}
            initialConfig={initialConfig as { definition?: string } | undefined}
            onClose={mode === "configure" ? () => {} : onClose}
            onPendingChange={handlePendingChange}
            onConfigure={handleConfigure}
            onSubmitHandlerRef={formSubmitRef}
          />
        )
      case CapabilityIdentifier.ReactToMessage: {
        const parsedReactConfig = ReactToMessageCapabilityConfigSchema.safeParse(initialConfig)
        return (
          <ReactToMessageForm
            capability={capability as z.infer<typeof CapabilitiesResponseDataSchema>[number]}
            mode={mode === "configure" ? "update" : "install"}
            initialConfig={parsedReactConfig.success ? parsedReactConfig.data : undefined}
            onClose={mode === "configure" ? () => {} : onClose}
            onPendingChange={handlePendingChange}
            onConfigure={handleConfigure}
            onSubmitHandlerRef={formSubmitRef}
          />
        )
      }
      case CapabilityIdentifier.Support: {
        const parsedSupportConfig = SupportCapabilityConfigSchema.safeParse(initialConfig)
        return (
          <SupportForm
            capability={capability as z.infer<typeof CapabilitiesResponseDataSchema>[number]}
            mode={mode === "configure" ? "update" : "install"}
            initialConfig={parsedSupportConfig.success ? parsedSupportConfig.data : undefined}
            onClose={mode === "configure" ? () => {} : onClose}
            onPendingChange={handlePendingChange}
            onConfigure={handleConfigure}
            onSubmitHandlerRef={formSubmitRef}
          />
        )
      }
      case CapabilityIdentifier.RequestReview: {
        const parsedReviewConfig = RequestReviewCapabilityConfigSchema.safeParse(initialConfig)
        return (
          <RequestReviewForm
            capability={capability as z.infer<typeof CapabilitiesResponseDataSchema>[number]}
            mode={mode === "configure" ? "update" : "install"}
            initialConfig={parsedReviewConfig.success ? parsedReviewConfig.data : undefined}
            onClose={mode === "configure" ? () => {} : onClose}
            onPendingChange={handlePendingChange}
            onConfigure={handleConfigure}
            onSubmitHandlerRef={formSubmitRef}
          />
        )
      }
      case CapabilityIdentifier.Webhook:
        throw new Error("Webhook capability should be rendered in a separate dialog")
      case CapabilityIdentifier.Instruction:
        throw new Error("Instruction capability should be rendered in a separate dialog")
      case CapabilityIdentifier.MCP:
        throw new Error("MCP capability should be rendered in a separate dialog")
    }
  }, [capability, mode, initialConfig, onClose, handlePendingChange, handleConfigure])

  const isFormPending = mode === "configure" ? isPending || isUpdateActivationPending : isPending

  const dialogTitle =
    mode === "configure"
      ? t("capabilities.activation.editTitle", { name })
      : t("capabilities.list.install.dialog.title", { name })

  const submitButtonText = mode === "configure" ? t("shared.save") : t("shared.install")

  const showOverrideHint =
    "supportsConfigOverride" in capability && capability.supportsConfigOverride

  return (
    <>
      <CapabilitySheetLayout
        open={open}
        onOpenChange={onOpenChange}
        badge={getBadge(capability.kind)}
        title={dialogTitle}
        description={detailedDescription ?? description}
        showOverrideHint={showOverrideHint}
      >
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-5">
            <Accordion
              type="single"
              collapsible
              defaultValue="config"
              className="border rounded-lg bg-secondary"
            >
              <AccordionItem value="config">
                <AccordionTrigger className="text-lg">
                  {t("capabilities.dialog.configSectionLabel")}
                </AccordionTrigger>
                <AccordionContent className="flex flex-col gap-4">{content}</AccordionContent>
              </AccordionItem>
            </Accordion>
            <Accordion type="single" collapsible className="border rounded-lg bg-secondary">
              <AccordionItem value="activation">
                <AccordionTrigger className="text-lg">
                  {t("capabilities.activation.sectionLabel")}
                </AccordionTrigger>
                <AccordionContent className="flex flex-col gap-4">
                  <CapabilityActivationSettings
                    campaignAssignation={campaignAssignation}
                    setCampaignAssignation={setCampaignAssignation}
                    campaignIds={campaignIds}
                    setCampaignIds={setCampaignIds}
                    campaignFlow={campaignFlow}
                    setCampaignFlow={setCampaignFlow}
                  />
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
          <SheetFooter className="flex shrink-0 flex-row justify-between gap-2 border-t bg-background p-5">
            <Button type="button" variant="outline" onClick={onClose}>
              {t("shared.cancel")}
            </Button>
            <Button type="submit" disabled={isFormPending}>
              {submitButtonText}
            </Button>
          </SheetFooter>
        </form>
      </CapabilitySheetLayout>
      {mode === "install" && installedCapability && (
        <CapabilityIntegrationDialog
          capability={installedCapability}
          mode="configure"
          open={isConfigureDialogOpen}
          onOpenChange={setIsConfigureDialogOpen}
        />
      )}
    </>
  )
}
